import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPlus, faArrowsRotate, faRotateLeft, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { DEFAULT_ASSISTANT_AVATAR_URL } from '@course-authoring/ui-blocks';
import type { ThemeSettingsPageStyles } from './themeSettingsPageStyles';
import type { ThemeAssistantSectionViewModel } from './useThemeAssistantSettings';

const AVATAR_GUIDELINES = [
  'The file must be a PNG, JPG or GIF no larger than 2MB.',
  'We recommend a square image at least 200px by 200px.',
  'The image is cropped to a circle, so a non-square image will be trimmed from the sides.',
];

interface FieldLabelProps {
  htmlFor: string;
  label: string;
  showStandard: boolean;
  onStandard: () => void;
  disabled: boolean;
  styles: ThemeSettingsPageStyles;
}

function FieldLabel({ htmlFor, label, showStandard, onStandard, disabled, styles }: FieldLabelProps) {
  return (
    <div style={styles.assistantFieldLabelRow}>
      <label htmlFor={htmlFor} style={styles.assistantFieldLabel}>{label}</label>
      {showStandard && <button
        type="button"
        style={{ ...styles.revertLink, ...styles.assistantRestoreLink }}
        onMouseEnter={(event) => { event.currentTarget.style.textDecoration = 'underline'; }}
        onMouseLeave={(event) => { event.currentTarget.style.textDecoration = 'none'; }}
        onClick={onStandard}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" />
        Standard text
      </button>}
    </div>
  );
}

export type ThemeAssistantSectionProps = ThemeAssistantSectionViewModel & {
  styles: ThemeSettingsPageStyles;
  dirtyFields: { avatar: boolean; name: boolean; greeting: boolean; intro: boolean };
  accentColor: string;
  disabled?: boolean;
};

export function ThemeAssistantSection({
  styles, dirtyFields, accentColor, disabled = false,
  settings, avatar, avatarError, avatarInputRef, avatarInputId,
  setName, setGreeting, setIntro, setIntroStandard, avatarHandlers,
}: ThemeAssistantSectionProps) {
  const [focused, setFocused] = useState<'name' | 'greeting' | 'intro' | null>(null);
  const defaultName = settings.name !== 'Ema';
  const defaultGreeting = settings.greeting !== 'Hi there!';
  const defaultIntro = settings.intro !== `I'm ${settings.name} — your friendly EmBot assistant. What can I help you with today?`;
  const avatarSrc = avatar?.src ?? DEFAULT_ASSISTANT_AVATAR_URL;

  return (
    <section style={styles.card} aria-labelledby="theme-settings-assistant">
      <header style={styles.assistantHeader}>
        <h2 id="theme-settings-assistant" style={styles.cardTitle}>Assistant</h2>
        <p style={styles.assistantDescription}>Customize the assistant that appears in the Ask Ema panel and the welcome modal.</p>
      </header>

      <div style={styles.assistantBody}>
        <div style={styles.assistantAvatarRow}>
          <div style={styles.assistantAvatarGuidance}>
            <h3 style={styles.assistantSubheading}>Avatar</h3>
            <ul style={styles.bulletList}>
              {AVATAR_GUIDELINES.map((line) => <li key={line} style={styles.bulletItem}>{line}</li>)}
            </ul>
          </div>
          <div style={styles.assistantAvatarControls}>
            <div style={{ ...styles.assistantAvatarTile, ...(dirtyFields.avatar ? styles.dirtyControl : {}) }}>
              {avatar ? <img src={avatarSrc} alt={`Selected assistant avatar: ${avatar.name}`} style={styles.assistantAvatarImage} /> : <button type="button" style={styles.assistantUploadButton} onClick={avatarHandlers.triggerFilePicker} disabled={disabled}>
                <span style={styles.assistantUploadTitle}>Upload Avatar</span>
                <span style={styles.assistantUploadSize}>2 MB max</span>
                <span style={styles.assistantUploadIcon} aria-hidden="true"><FontAwesomeIcon icon={faPlus} /></span>
              </button>}
            </div>
            {avatar && <div style={styles.assistantAvatarActions}>
              <button type="button" style={styles.uploadedLogoAction('replace')} onClick={avatarHandlers.triggerFilePicker} disabled={disabled}>
                <FontAwesomeIcon icon={faArrowsRotate} /> Replace
              </button>
              <button type="button" style={styles.uploadedLogoAction('delete')} onClick={avatarHandlers.handleDelete} disabled={disabled}>
                <FontAwesomeIcon icon={faTrashCan} /> Delete
              </button>
            </div>}
            {avatarError && <p id={`${avatarInputId}-error`} style={styles.errorMessage} role="alert">{avatarError}</p>}
            <input
              ref={avatarInputRef}
              id={avatarInputId}
              type="file"
              accept="image/png,image/jpeg,image/gif"
              onChange={avatarHandlers.handleInputChange}
              style={styles.visuallyHiddenInput}
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        </div>

        <div style={styles.assistantFieldRow}>
          <div style={styles.assistantFieldsStack}>
            <div style={styles.assistantFieldColumn}>
            <FieldLabel htmlFor="assistant-name" label="Assistant name" showStandard={defaultName} onStandard={() => setName('Ema')} disabled={disabled} styles={styles} />
            <input
              id="assistant-name" type="text" maxLength={12} value={settings.name} disabled={disabled}
              onChange={(event) => setName(event.target.value)}
              onFocus={() => setFocused('name')}
              onBlur={() => { setFocused(null); if (!settings.name.trim()) setName('Ema'); }}
              style={{ ...styles.assistantTextInput, ...(dirtyFields.name ? styles.dirtyControl : {}) }}
              aria-describedby="assistant-name-help assistant-name-count"
            />
            <div style={styles.assistantFieldHintRow}>
              <span id="assistant-name-help" style={styles.assistantFieldHelp}>Replaces "Ema" everywhere it appears to learners.</span>
              <span id="assistant-name-count" style={styles.assistantCounter} aria-live="polite">{focused === 'name' ? `${settings.name.length}/12` : ' '}</span>
            </div>
            </div>
            <div style={styles.assistantFieldColumn}>
            <FieldLabel htmlFor="assistant-greeting" label="Greeting" showStandard={defaultGreeting} onStandard={() => setGreeting('Hi there!')} disabled={disabled} styles={styles} />
            <input
              id="assistant-greeting" type="text" maxLength={40} value={settings.greeting} disabled={disabled}
              onChange={(event) => setGreeting(event.target.value)}
              onFocus={() => setFocused('greeting')} onBlur={() => setFocused(null)}
              style={{ ...styles.assistantTextInput, ...(dirtyFields.greeting ? styles.dirtyControl : {}) }}
              aria-describedby="assistant-greeting-count"
            />
            <span id="assistant-greeting-count" style={styles.assistantCounter} aria-live="polite">{focused === 'greeting' ? `${settings.greeting.length}/40` : ' '}</span>
            </div>
            <div style={styles.assistantFieldColumn}>
            <FieldLabel htmlFor="assistant-intro" label="Intro line" showStandard={defaultIntro} onStandard={setIntroStandard} disabled={disabled} styles={styles} />
            <textarea
              id="assistant-intro" maxLength={160} value={settings.intro} disabled={disabled}
              onChange={(event) => setIntro(event.target.value)}
              onFocus={() => setFocused('intro')} onBlur={() => setFocused(null)}
              style={{ ...styles.assistantTextArea, ...(dirtyFields.intro ? styles.dirtyControl : {}) }}
              aria-describedby="assistant-intro-count"
            />
            <span id="assistant-intro-count" style={styles.assistantCounter} aria-live="polite">{focused === 'intro' ? `${settings.intro.length}/160` : ' '}</span>
            </div>
          </div>
          <AssistantPreview styles={styles} accentColor={accentColor} name={settings.name || 'Ema'} greeting={settings.greeting} intro={settings.intro} avatarSrc={avatarSrc} />
        </div>
      </div>
    </section>
  );
}

function AssistantPreview({
  styles, accentColor, name, greeting, intro, avatarSrc,
}: {
  styles: ThemeSettingsPageStyles;
  accentColor: string;
  name: string;
  greeting: string;
  intro: string;
  avatarSrc: string;
}) {
  return (
    <div style={styles.assistantPreview}>
      <strong style={styles.assistantPreviewLabel}>Preview</strong>
      <div style={styles.assistantPreviewPanel}>
        <div style={{ ...styles.assistantPreviewHeader, backgroundColor: accentColor }}>
          <FontAwesomeIcon icon={faComments} aria-hidden="true" /> <span>Ask {name}</span>
        </div>
        <div style={styles.assistantPreviewMessage}>
          <img src={avatarSrc} alt="" style={styles.assistantPreviewAvatar} />
          <div style={styles.assistantPreviewCopy}>
            <strong>{greeting}</strong>
            <span>{intro}</span>
          </div>
        </div>
      </div>
      <button type="button" style={{ ...styles.assistantPreviewFab, backgroundColor: accentColor }} aria-label={`Open Ask ${name}`}>
        <FontAwesomeIcon icon={faComments} aria-hidden="true" /> <span>Ask {name}</span>
      </button>
    </div>
  );
}
